import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { useTemplates } from './hooks/useTemplates'
import { TRIGGER_EVENT_TYPES } from './i18n/notification_event_types'
import { NOTIFICATION_ROLES } from './i18n/notification_roles'
import { EventCard } from './components/EventCard'

const EXPECTED_ROWS = TRIGGER_EVENT_TYPES.length * NOTIFICATION_ROLES.length // 60 = 10 × 6

export function TriggersPage() {
  const { t } = useTranslation()
  const variables = [
    { name: '{brand}', label: t('notif.triggers.var.brand') },
    { name: '{model}', label: t('notif.triggers.var.model') },
    { name: '{order_number}', label: t('notif.triggers.var.orderNumber') },
    { name: '{customer_company}', label: t('notif.triggers.var.customerCompany') },
    { name: '{executor_company}', label: t('notif.triggers.var.executorCompany') },
    { name: '{address_city}', label: t('notif.triggers.var.addressCity') },
  ]
  const [varsOpen, setVarsOpen] = useState(false)
  const { data: rows = [], isLoading, error } = useTemplates()

  const byEvent = useMemo(() => {
    const m = new Map<string, typeof rows>()
    for (const et of TRIGGER_EVENT_TYPES) {
      m.set(et, [])
    }
    for (const r of rows) {
      const list = m.get(r.event_type)
      if (list) list.push(r)
    }
    return m
  }, [rows])

  const isIncomplete = !isLoading && rows.length > 0 && rows.length < EXPECTED_ROWS

  return (
    <div>
      <PageHeader title={t('notif.triggers.title')} />

      {error ? (
        <div
          role="alert"
          className="mb-4 rounded-md border border-[color:var(--stryzh-color-status-error)] px-3 py-2 text-sm text-[color:var(--stryzh-color-status-error)]"
        >
          {(error as Error).message}
        </div>
      ) : null}

      {/* Variables accordion */}
      <div className="mb-4 rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]">
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-[color:var(--stryzh-color-text-primary)]"
          onClick={() => setVarsOpen((v) => !v)}
        >
          <span
            className={`text-xs text-[color:var(--stryzh-color-text-secondary)] transition-transform ${varsOpen ? 'rotate-90' : ''}`}
            aria-hidden
          >
            ▶
          </span>
          {t('notif.triggers.availableVars')}
        </button>
        {varsOpen && (
          <ul className="border-t border-[color:var(--stryzh-color-border-subtle)] px-4 pb-3 pt-2 space-y-1">
            {variables.map((v) => (
              <li key={v.name} className="flex gap-2 text-sm">
                <code className="font-mono text-[color:var(--stryzh-color-accent)]">{v.name}</code>
                <span className="text-[color:var(--stryzh-color-text-secondary)]">— {v.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isIncomplete && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-amber-600/50 bg-amber-950/40 px-3 py-2 text-sm text-amber-200"
        >
          {t('notif.triggers.incomplete', { found: rows.length, expected: EXPECTED_ROWS })}
        </div>
      )}

      {isLoading ? (
        <Loader size="md" centered label={t('notif.triggers.loadingTemplates')} />
      ) : isIncomplete ? null : (
        <div className="flex flex-col gap-3">
          {TRIGGER_EVENT_TYPES.map((eventType) => (
            <EventCard
              key={eventType}
              eventType={eventType}
              rows={byEvent.get(eventType) ?? []}
            />
          ))}
        </div>
      )}
    </div>
  )
}
